import { useEffect, useState } from "react";
import { Button, Field, SelectField, Spinner, StatusBadge } from "@leylion/design-system";
import {
  createPlatformIdempotencyKey,
  executePlatformPlanCommand,
  getPlatformPlanDetail
} from "./platform-client";
import type { PlatformPlanDetail, PlatformPlanSummary, PlatformPlanVersionDetail } from "./platform-types";
import { localDateTimeErrorMessage, localTimeZoneHint, resolveLocalDateTime, toLocalDateTimeInput } from "../shared/local-date-time";
import "./platform-admin.css";

type Language = "ar" | "en";
type Mode = "none" | "create-plan" | "edit-plan" | "create-version" | "edit-version" | "publish-version" | "retire-version";

const PLAN_REASONS = [
  "PLAN_CATALOG_SETUP",
  "PLAN_CATALOG_CORRECTION",
  "PLAN_VERSION_RELEASE",
  "PLAN_VERSION_RETIREMENT",
  "COMMERCIAL_PLAN_CHANGE",
  "SUPPORT_CORRECTION"
];

const MODE_REASON: Partial<Record<Mode, string>> = {
  "edit-plan": "PLAN_CATALOG_CORRECTION",
  "create-version": "PLAN_VERSION_RELEASE",
  "edit-version": "PLAN_VERSION_RELEASE",
  "publish-version": "PLAN_VERSION_RELEASE",
  "retire-version": "PLAN_VERSION_RETIREMENT"
};

const MODE_LABEL: Record<Exclude<Mode, "none">, { ar: string; en: string }> = {
  "create-plan": { ar: "إنشاء باقة", en: "Create plan" },
  "edit-plan": { ar: "تعديل الباقة", en: "Edit plan" },
  "create-version": { ar: "إنشاء نسخة Draft", en: "Create draft version" },
  "edit-version": { ar: "تعديل نسخة Draft", en: "Edit draft version" },
  "publish-version": { ar: "نشر النسخة", en: "Publish version" },
  "retire-version": { ar: "إنهاء النسخة", en: "Retire version" }
};

function nowLocal(): string {
  return toLocalDateTimeInput(new Date());
}

function parsePolicyText(value: string, kind: "entitlements" | "limits"): Record<string, boolean | number | null> {
  const result: Record<string, boolean | number | null> = {};
  for (const line of value.split("\n")) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    const [rawKey, rawValue] = trimmed.split("=").map((part) => part.trim());
    if (!rawKey || rawValue === undefined) throw new Error(`INVALID_${kind.toUpperCase()}_LINE:${trimmed}`);
    if (kind === "entitlements") {
      if (rawValue !== "true" && rawValue !== "false") throw new Error(`INVALID_ENTITLEMENT_VALUE:${rawKey}`);
      result[rawKey] = rawValue === "true";
    } else {
      if (rawValue === "unlimited" || rawValue === "null") result[rawKey] = null;
      else {
        const number = Number(rawValue);
        if (!Number.isInteger(number) || number < 0) throw new Error(`INVALID_LIMIT_VALUE:${rawKey}`);
        result[rawKey] = number;
      }
    }
  }
  return result;
}

function policyToText(value: Record<string, boolean | number | null>): string {
  return Object.entries(value).map(([key, item]) => `${key}=${item === null ? "unlimited" : item}`).join("\n");
}

export function PlatformPlanAdminPanel({
  token,
  language,
  plans,
  onChanged
}: {
  token: string;
  language: Language;
  plans: PlatformPlanSummary[];
  onChanged: () => Promise<void> | void;
}) {
  const ar = language === "ar";
  const [selectedPlanId, setSelectedPlanId] = useState(plans[0]?.planId ?? "");
  const [detail, setDetail] = useState<PlatformPlanDetail | null>(null);
  const [selectedVersionId, setSelectedVersionId] = useState("");
  const [mode, setMode] = useState<Mode>("none");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [reasonCode, setReasonCode] = useState(PLAN_REASONS[0]);
  const [reasonNote, setReasonNote] = useState("");
  const [confirmationPhrase, setConfirmationPhrase] = useState("");

  const [code, setCode] = useState("");
  const [nameAr, setNameAr] = useState("");
  const [nameEn, setNameEn] = useState("");
  const [descriptionAr, setDescriptionAr] = useState("");
  const [descriptionEn, setDescriptionEn] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [isActive, setIsActive] = useState(true);

  const [activationMode, setActivationMode] = useState("Trial");
  const [trialDays, setTrialDays] = useState("14");
  const [effectiveFrom, setEffectiveFrom] = useState(nowLocal());
  const [effectiveUntil, setEffectiveUntil] = useState(nowLocal());
  const [originalEffectiveFrom, setOriginalEffectiveFrom] = useState<string | null>(null);
  const [originalEffectiveUntil, setOriginalEffectiveUntil] = useState<string | null>(null);
  const [entitlementsText, setEntitlementsText] = useState("branches=true\nusers=true\ndevices=true");
  const [limitsText, setLimitsText] = useState("branches=1\nusers=5\ndevices=1");

  const loadPlan = async (planId = selectedPlanId) => {
    if (!planId) { setDetail(null); return; }
    setBusy(true);
    setError("");
    try {
      const value = await getPlatformPlanDetail(token, planId);
      setDetail(value);
      setSelectedPlanId(planId);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Plan administration could not be loaded.");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!selectedPlanId && plans[0]) setSelectedPlanId(plans[0].planId);
  }, [plans, selectedPlanId]);
  useEffect(() => { void loadPlan(); }, [token, selectedPlanId]);

  const selectedVersion = detail?.versions.find((version) => version.planVersionId === selectedVersionId) ?? null;

  const resetCommon = (nextMode: Mode) => {
    setReasonCode(MODE_REASON[nextMode] ?? PLAN_REASONS[0]);
    setReasonNote("");
    setConfirmationPhrase("");
    setSuccess("");
    setError("");
  };

  const beginCreatePlan = () => {
    resetCommon("create-plan");
    setMode("create-plan");
    setCode(""); setNameAr(""); setNameEn(""); setDescriptionAr(""); setDescriptionEn(""); setIsPublic(false); setIsActive(true);
  };

  const beginEditPlan = () => {
    if (!detail) return;
    resetCommon("edit-plan");
    setMode("edit-plan");
    setCode(detail.code); setNameAr(detail.nameAr); setNameEn(detail.nameEn); setDescriptionAr(detail.descriptionAr ?? ""); setDescriptionEn(detail.descriptionEn ?? ""); setIsPublic(detail.isPublic); setIsActive(detail.isActive);
  };

  const beginCreateVersion = () => {
    if (!detail) return;
    resetCommon("create-version");
    setMode("create-version");
    setActivationMode("Trial"); setTrialDays("14"); setEffectiveFrom(nowLocal()); setEntitlementsText("branches=true\nusers=true\ndevices=true"); setLimitsText("branches=1\nusers=5\ndevices=1");
    setOriginalEffectiveFrom(null); setOriginalEffectiveUntil(null);
  };

  const beginVersion = (nextMode: Mode, version: PlatformPlanVersionDetail) => {
    resetCommon(nextMode);
    setMode(nextMode);
    setSelectedVersionId(version.planVersionId);
    setActivationMode(version.activationMode);
    setTrialDays(version.trialDays?.toString() ?? "");
    setEffectiveFrom(toLocalDateTimeInput(version.effectiveFrom));
    setEffectiveUntil(version.effectiveUntil ? toLocalDateTimeInput(version.effectiveUntil) : nowLocal());
    setOriginalEffectiveFrom(version.effectiveFrom);
    setOriginalEffectiveUntil(version.effectiveUntil);
    setEntitlementsText(policyToText(version.entitlements));
    setLimitsText(policyToText(version.limits));
  };

  const execute = async () => {
    setBusy(true); setError(""); setSuccess("");
    try {
      const toIso = (value: string, original: string | null) => {
        const resolved = resolveLocalDateTime(value, original);
        if (resolved.error) throw new Error(localDateTimeErrorMessage(resolved.error, language));
        return resolved.iso;
      };
      let result: PlatformPlanDetail;
      if (mode === "create-plan") {
        result = await executePlatformPlanCommand(token, "", "POST", {
          code, nameAr, nameEn, descriptionAr: descriptionAr || null, descriptionEn: descriptionEn || null, isPublic
        }, createPlatformIdempotencyKey("plan-create"));
        setSelectedPlanId(result.planId);
      } else {
        if (!detail) throw new Error(ar ? "اختر باقة." : "Select a plan.");
        if (mode === "edit-plan") {
          if (detail.isActive && !isActive && confirmationPhrase.trim() !== "DEACTIVATE") throw new Error(ar ? "اكتب DEACTIVATE للتأكيد." : "Type DEACTIVATE to confirm.");
          result = await executePlatformPlanCommand(token, `/${detail.planId}`, "PUT", {
            expectedVersion: detail.version, nameAr, nameEn, descriptionAr: descriptionAr || null, descriptionEn: descriptionEn || null, isPublic, isActive, reasonCode, reasonNote: reasonNote || null, confirmationPhrase: confirmationPhrase || null
          }, createPlatformIdempotencyKey("plan-update"));
        } else if (mode === "create-version") {
          result = await executePlatformPlanCommand(token, `/${detail.planId}/versions`, "POST", {
            activationMode, trialDays: activationMode === "Trial" ? Number(trialDays) : null,
            entitlements: parsePolicyText(entitlementsText, "entitlements"), limits: parsePolicyText(limitsText, "limits"), effectiveFrom: toIso(effectiveFrom, null), reasonCode, reasonNote: reasonNote || null
          }, createPlatformIdempotencyKey("plan-version-create"));
        } else {
          if (!selectedVersion) throw new Error(ar ? "اختر نسخة باقة." : "Select a plan version.");
          if (mode === "edit-version") {
            result = await executePlatformPlanCommand(token, `/${detail.planId}/versions/${selectedVersion.planVersionId}`, "PUT", {
              activationMode, trialDays: activationMode === "Trial" ? Number(trialDays) : null,
              entitlements: parsePolicyText(entitlementsText, "entitlements"), limits: parsePolicyText(limitsText, "limits"), effectiveFrom: toIso(effectiveFrom, originalEffectiveFrom), reasonCode, reasonNote: reasonNote || null
            }, createPlatformIdempotencyKey("plan-version-update"));
          } else if (mode === "publish-version") {
            if (confirmationPhrase.trim() !== "PUBLISH") throw new Error(ar ? "اكتب PUBLISH للتأكيد." : "Type PUBLISH to confirm.");
            result = await executePlatformPlanCommand(token, `/${detail.planId}/versions/${selectedVersion.planVersionId}/publish`, "POST", {
              reasonCode, reasonNote: reasonNote || null, confirmationPhrase
            }, createPlatformIdempotencyKey("plan-version-publish"));
          } else if (mode === "retire-version") {
            if (confirmationPhrase.trim() !== "RETIRE") throw new Error(ar ? "اكتب RETIRE للتأكيد." : "Type RETIRE to confirm.");
            result = await executePlatformPlanCommand(token, `/${detail.planId}/versions/${selectedVersion.planVersionId}/retire`, "POST", {
              effectiveUntil: effectiveUntil ? toIso(effectiveUntil, originalEffectiveUntil) : null, reasonCode, reasonNote: reasonNote || null, confirmationPhrase
            }, createPlatformIdempotencyKey("plan-version-retire"));
          } else throw new Error("INVALID_PLAN_ADMIN_MODE");
        }
      }
      setDetail(result);
      setMode("none");
      setSuccess(ar ? "تم تنفيذ العملية وتسجيلها في سجل المنصة." : "The operation was applied and recorded in platform audit.");
      await onChanged();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Plan administration command failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="platform-admin-panel" data-testid="platform-plan-admin">
      <div className="platform-admin-panel__heading">
        <div>
          <span className="platform-kicker">PLAN ADMINISTRATION</span>
          <h3>{ar ? "إدارة الباقات والإصدارات" : "Plan & version administration"}</h3>
          <p>{ar ? "النسخ المنشورة غير قابلة للتعديل؛ أنشئ Draft جديدًا لأي تغيير." : "Published versions are immutable; create a new draft for changes."}</p>
        </div>
        <Button type="button" onClick={beginCreatePlan}>{ar ? "إنشاء باقة" : "Create plan"}</Button>
      </div>

      {error ? <div className="platform-admin-error" role="alert">{error}</div> : null}
      {success ? <div className="platform-admin-success" role="status">{success}</div> : null}

      <div className="platform-admin-plan-selector">
        <SelectField label={ar ? "الباقة" : "Plan"} value={selectedPlanId} onChange={(event) => { setSelectedPlanId(event.target.value); setMode("none"); }}>
          {plans.map((plan) => <option key={plan.planId} value={plan.planId}>{plan.code} · {ar ? plan.nameAr : plan.nameEn}</option>)}
        </SelectField>
        {detail ? <>
          <StatusBadge tone={detail.isActive ? "success" : "neutral"}>{detail.isActive ? (ar ? "فعالة" : "Active") : (ar ? "غير فعالة" : "Inactive")}</StatusBadge>
          <Button variant="secondary" type="button" onClick={beginEditPlan}>{ar ? "تعديل الباقة" : "Edit plan"}</Button>
          <Button variant="secondary" type="button" onClick={beginCreateVersion}>{ar ? "نسخة Draft جديدة" : "New draft version"}</Button>
        </> : null}
      </div>

      {busy && !detail ? <Spinner label={ar ? "تحميل الباقة" : "Loading plan"} /> : null}

      {detail ? (
        <div className="platform-admin-version-list">
          {detail.versions.map((version) => {
            const retired = Boolean(version.effectiveUntil && new Date(version.effectiveUntil).getTime() <= Date.now());
            return (
              <article key={version.planVersionId}>
                <div>
                  <strong>v{version.versionNumber}</strong>
                  <StatusBadge tone={retired ? "neutral" : version.isPublished ? "success" : "info"}>
                    {retired ? (ar ? "منتهية" : "Retired") : version.isPublished ? (ar ? "منشورة" : "Published") : "Draft"}
                  </StatusBadge>
                  <span>{version.activationMode}</span>
                </div>
                <small>{new Date(version.effectiveFrom).toLocaleString()} → {version.effectiveUntil ? new Date(version.effectiveUntil).toLocaleString() : "∞"}</small>
                <code>{policyToText(version.limits)}</code>
                <div className="platform-admin-actions">
                  {!version.isPublished ? <>
                    <Button variant="secondary" type="button" onClick={() => beginVersion("edit-version", version)}>{ar ? "تعديل" : "Edit"}</Button>
                    <Button type="button" onClick={() => beginVersion("publish-version", version)}>{ar ? "نشر" : "Publish"}</Button>
                  </> : !retired ? (
                    <Button variant="secondary" type="button" onClick={() => beginVersion("retire-version", version)}>{ar ? "إنهاء النسخة" : "Retire"}</Button>
                  ) : null}
                </div>
              </article>
            );
          })}
        </div>
      ) : null}

      {mode !== "none" ? (
        <div className="platform-admin-editor">
          <h4>{ar ? MODE_LABEL[mode].ar : MODE_LABEL[mode].en}</h4>
          {(mode === "create-plan" || mode === "edit-plan") ? (
            <div className="platform-admin-form-grid">
              {mode === "create-plan" ? (
                <label className="platform-admin-native-field">
                  <span>Code</span>
                  <input value={code} onChange={(event) => setCode(event.target.value)} />
                  <small>{ar ? "معرّف دائم بعد الإنشاء. المسموح: A-Z و0-9 والشرطة - والشرطة السفلية _." : "Permanent after creation. Allowed: A-Z, 0-9, '-' and '_'."}</small>
                </label>
              ) : null}
              <Field label={ar ? "الاسم العربي" : "Arabic name"} value={nameAr} onChange={(event) => setNameAr(event.target.value)} />
              <Field label={ar ? "الاسم الإنجليزي" : "English name"} value={nameEn} onChange={(event) => setNameEn(event.target.value)} />
              <Field label={ar ? "الوصف العربي" : "Arabic description"} value={descriptionAr} onChange={(event) => setDescriptionAr(event.target.value)} />
              <Field label={ar ? "الوصف الإنجليزي" : "English description"} value={descriptionEn} onChange={(event) => setDescriptionEn(event.target.value)} />
              <label className="platform-admin-confirm"><input type="checkbox" checked={isPublic} onChange={(event) => setIsPublic(event.target.checked)} /><span>{ar ? "باقة عامة" : "Public plan"}</span></label>
              {mode === "edit-plan" ? <label className="platform-admin-confirm"><input type="checkbox" checked={isActive} onChange={(event) => setIsActive(event.target.checked)} /><span>{ar ? "الباقة فعالة" : "Plan active"}</span></label> : null}
            </div>
          ) : null}

          {(mode === "create-version" || mode === "edit-version") ? (
            <div className="platform-admin-form-grid">
              <SelectField label={ar ? "نمط التفعيل" : "Activation mode"} value={activationMode} onChange={(event) => setActivationMode(event.target.value)}>
                <option value="Trial">Trial</option>
                <option value="ManualApproval">ManualApproval</option>
                <option value="PaymentRequired">PaymentRequired</option>
              </SelectField>
              {activationMode === "Trial" ? <Field label={ar ? "أيام التجربة" : "Trial days"} value={trialDays} onChange={(event) => setTrialDays(event.target.value)} /> : null}
              <label className="platform-admin-native-field"><span>{ar ? "بدء السريان" : "Effective from"}</span><input aria-label={ar ? "بدء السريان" : "Effective from"} aria-describedby="plan-effective-from-zone" type="datetime-local" value={effectiveFrom} onChange={(event) => setEffectiveFrom(event.target.value)} /><small id="plan-effective-from-zone">{localTimeZoneHint(language)}</small></label>
              <label className="platform-admin-textarea"><span>{ar ? "الميزات (key=true)" : "Entitlements (key=true)"}</span><textarea value={entitlementsText} onChange={(event) => setEntitlementsText(event.target.value)} /></label>
              <label className="platform-admin-textarea"><span>{ar ? "الحدود (key=number/unlimited)" : "Limits (key=number/unlimited)"}</span><textarea value={limitsText} onChange={(event) => setLimitsText(event.target.value)} /></label>
            </div>
          ) : null}

          {mode !== "create-plan" ? (
            <div className="platform-admin-form-grid">
              <SelectField label={ar ? "السبب" : "Reason"} value={reasonCode} onChange={(event) => setReasonCode(event.target.value)}>
                {PLAN_REASONS.map((reason) => <option key={reason} value={reason}>{reason}</option>)}
              </SelectField>
              <Field label={ar ? "ملاحظة السبب" : "Reason note"} value={reasonNote} onChange={(event) => setReasonNote(event.target.value)} />
            </div>
          ) : null}

          {mode === "retire-version" ? <label className="platform-admin-native-field"><span>{ar ? "نهاية السريان" : "Effective until"}</span><input aria-label={ar ? "نهاية السريان" : "Effective until"} aria-describedby="plan-effective-until-zone" type="datetime-local" value={effectiveUntil} onChange={(event) => setEffectiveUntil(event.target.value)} /><small id="plan-effective-until-zone">{localTimeZoneHint(language)} · {ar ? "ترك الموعد فارغًا يعني الإنهاء فورًا عند التنفيذ." : "Leaving this empty retires the version immediately when executed."}</small></label> : null}
          {(mode === "publish-version" || mode === "retire-version" || (mode === "edit-plan" && detail?.isActive && !isActive)) ? (
            <Field label={mode === "publish-version" ? "PUBLISH" : mode === "retire-version" ? "RETIRE" : "DEACTIVATE"} value={confirmationPhrase} onChange={(event) => setConfirmationPhrase(event.target.value)} />
          ) : null}

          <div className="platform-admin-actions">
            <Button type="button" disabled={busy} onClick={() => void execute()}>{busy ? "…" : ar ? "تنفيذ" : "Execute"}</Button>
            <Button variant="secondary" type="button" disabled={busy} onClick={() => setMode("none")}>{ar ? "إلغاء" : "Cancel"}</Button>
          </div>
        </div>
      ) : null}
    </section>
  );
}
